/**
 * @fileoverview `createPublicJsonPoster(label)` — login-shell's ONE pre-authentication JSON POST.
 * @module @puffin-core/login-shell/utils/publicJsonPoster
 *
 * Promoted out of `MarketingCrossDomainLogin` (UNREACHABLE-MODULE-BURNDOWN-001 L16), where it was
 * the private `postJson`, because the tenant login's "Request access" modal needs exactly the same
 * call — a JSON POST, before any session exists, whose failure the caller must be able to read.
 * A second copy is how the fleet's three `useRateLimit`s diverged (Q-UMB-8); one module is the fix.
 *
 * ## Why this reads the RAW response
 *
 * RES-CLIENT-001 (2026-09-01) chose an INJECTED fetch seam here, deliberately NOT `createApiFacade`:
 * these calls sit on the login path of nine prerendered marketing sites and ten tenant apps, and
 * the caller needs the AUTH-ERROR-VOCABULARY-001 error — `status`, `errorCode`, `errorDetails`,
 * plus the whole body on `err.data` — which a payload-resolving facade would collapse. The
 * `@puffin-core/api-client/fetch-seam` subpath is dependency-free, so the edge costs those bundles
 * one module.
 *
 * ## Why the caller names itself
 *
 * `createFetchSeam` reports an absent global `fetch` under a caller-supplied `label`, so the
 * rejection says WHO needed the fetch. A shared poster that named itself would trade that fact for
 * a shared implementation, which Gen-0 does not do — so the label is the factory's one parameter.
 */

import { createFetchSeam } from '@puffin-core/api-client/fetch-seam';

import { readJsonBody } from './readJsonBody.js';

/**
 * Build a pre-auth JSON POST function whose transport failures are attributed to `label`.
 *
 * @param {string} label - the calling surface, reported if no global `fetch` exists.
 * @returns {(url: string, body: Object, bearerToken?: string) => Promise<any>} resolves the
 *   RES-ENV-001 envelope's `data`; rejects with an `Error` carrying `status`, `errorCode`,
 *   `errorDetails` and `data` on a non-2xx OR a body that is not JSON, or a network `Error` whose
 *   `cause` is the original.
 */
export function createPublicJsonPoster(label) {
  const httpPost = createFetchSeam({ label });

  return async function postJson(url, body, bearerToken) {
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    // MKT-LOGIN-XDOMAIN-001/HANDOFF: the session-handoff POST authenticates with the access token
    // the marketing login just obtained (the user is not yet a cookie session). Every other caller
    // — subdomain lookup, access request — passes no token.
    if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;
    let response;
    try {
      response = await httpPost(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(body),
      });
    } catch (networkError) {
      const err = new Error('Network error. Please check your connection and try again.');
      err.cause = networkError;
      throw err;
    }
    const json = await readJsonBody(response);
    // A 2xx whose body is not a readable envelope is REFUSED like a non-2xx (SWALLOWED-CATCH-
    // BURNDOWN-001 U3): it used to read as `{}` and resolve `undefined` — a success nobody could
    // confirm.
    if (!response.ok || json === null) {
      // The fleet vocabulary (AUTH-ERROR-VOCABULARY-001), read from the ONE wire shape — the
      // RES-ENV-001 error envelope; the top-level `message` arm was retired with it.
      const err = new Error(json?.error?.message || 'Request failed');
      err.status = response.status;
      err.errorCode = typeof json?.error?.code === 'string' ? json.error.code : null;
      err.errorDetails = json?.error?.details ?? null;
      err.data = json;
      throw err;
    }
    // RES-CLIENT-001 (2026-08-26): was `json.data || json`. Every endpoint these callers reach is
    // served by an `@puffin-core/auth-routes` factory that answers through `sendSuccess`
    // exclusively, so `json` is always the canonical envelope. The retired `|| json` arm handed the
    // caller the BODY on an unreadable response, and — being `||`, not `??` — the envelope instead
    // of a legitimately falsy `data`.
    return json.data;
  };
}
